<!--@format-->
<template>
    <div class="apply-to-partnership-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ isLoading ? 'Loading...' : 'Apply to Partnership' }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="isLoading">
                    <loading-screen-svg />
                </div>
                <div v-else class="widget-container">
                    <div class="image-header">
                        <img
                            class="image-settings"
                            src="@/assets/images/application-graphic.png"
                            alt="application-graphic"
                        />
                    </div>
                    <div>
                        <div class="title-label">Company name</div>
                        <div class="item">
                            <img
                                class="logo-img"
                                :src="client.logoImageUrl | companyLogoPlaceholder"
                            />
                            <div class="name">{{ client.name }}</div>
                        </div>
                    </div>
                    <app-input
                        v-model="client.website"
                        label="Company website"
                        :is-valid="!getIsValidField('website')"
                        :class="{ 'has-errors': getIsValidField('website') }"
                        error="Website is missing"
                    />

                    <app-input
                        v-model="client.contact"
                        label="Contact Name"
                        :is-valid="!getIsValidField('contact')"
                        :class="{ 'has-errors': getIsValidField('contact') }"
                        error="Contact is missing"
                    />

                    <app-input
                        v-model="client.contactEmail"
                        label="Email address"
                        :is-valid="!getIsValidField('contactEmail')"
                        :class="{ 'has-errors': getIsValidField('contactEmail') }"
                        error="Email is missing"
                    />

                    <app-input
                        v-model="client.phoneNumber"
                        label="Phone number"
                        :is-valid="!getIsValidField('phoneNumber')"
                        :class="{ 'has-errors': getIsValidField('phoneNumber') }"
                        error="Phone number is missing"
                    />
                    <div>
                        <div
                            class="title-label"
                            :class="{ 'has-errors': getIsValidField('reasonForRequest') }"
                        >
                            Reason for request
                        </div>
                        <div
                            class="gray-container"
                            :class="{ 'has-errors': getIsValidField('reasonForRequest') }"
                        >
                            <div class="custom-textarea">
                                <textarea
                                    id="text-area"
                                    v-model="client.reasonForRequest"
                                    class="text-area"
                                    cols="30"
                                    rows="6"
                                    placeholder="What is the reason for this request"
                                ></textarea>
                            </div>
                        </div>
                        <div
                            v-if="getIsValidField('reasonForRequest')"
                            class="error-message-request"
                        >
                            Required
                        </div>
                    </div>
                    <div class="title-label">Disclaimer</div>
                    <div v-if="!storefront.disclaimer">
                        By applying for this partnership, if approved, you give
                        <span class="vendor-label">{{ agencyName }}</span> access to view your
                        instance of GlassHive.
                        <br />
                        <br />
                        All data viewed is strictly used to improve our partners' success and
                        provide us with information on how to improve content and process around
                        marketing and sales.
                    </div>
                    <div v-else v-html="formattedDisclaimer"></div>

                    <div>
                        <div
                            class="checkbox-section"
                            :class="{ 'has-errors': getIsValidField('disclaimer') }"
                        >
                            <app-checkbox v-model="client.disclaimer" />
                            <div class="prospect-text">I agree to the terms and conditions</div>
                        </div>
                        <div v-if="getIsValidField('disclaimer')" class="error-message-request">
                            Required
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button :disabled="loadingRequest" class="cancel-btn" @click="closeWidget">
                        Cancel
                    </app-button>
                </div>
                <div v-if="!isLoading" class="action-container">
                    <app-button
                        class="save-btn"
                        :loading="loadingRequest"
                        @click="onClickApplyAction"
                    >
                        Apply
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const AppInput = () => import('@/components/common/app-input');
const AppCheckbox = () => import('@/components/common/app-checkbox');

import AppPopup from '@/components/popups/app-popup';
import { sameAs } from 'vuelidate/lib/validators';
import { PartnerApplicationService } from '@/scripts/services/partner-application/partner-application-service';
import EventBus from '@/scripts/event-bus.js';
import { required, email } from 'vuelidate/lib/validators';
import { StorefrontService } from '@/scripts/services/storefront/StorefrontService';

export default {
    name: 'apply-to-partnership-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'app-input': AppInput,
        'app-checkbox': AppCheckbox,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            storefront: {
                disclaimer: null,
                logoImageUrl: null,
                description: null,
                resources: null,
            },

            isLoading: true,
            loadingRequest: false,
            agencyName: this.widget.agencyName,
            agendyId: this.widget.agencyId,
            client: {
                footer: null,
                id: this.widget.clientId,
                logoImageUrl: null,
                name: null,
                website: '',
                contact: '',
                contactEmail: '',
                phoneNumber: '',
                reasonForRequest: '',
                condition: false,
                disclaimer: false,
            },
        };
    },

    computed: {
        formattedDisclaimer() {
            return (this.storefront.disclaimer || '')
                .replace('[Partner Name]', this.client.name)
                .replace('[Vendor Name]', this.storefront.name);
        },

        routeBaseUrl() {
            return this.$store.getters.routeBaseUrl;
        },
    },

    created() {
        if (this.client.id) {
            this.isLoading = true;
            this.getClient().then(() => {
                this.isLoading = false;
            });
        }
        this.getStorefront();
    },

    validations() {
        return {
            client: {
                website: {
                    required,
                },

                contact: {
                    required,
                },

                contactEmail: {
                    required,
                    email,
                },

                phoneNumber: {
                    required,
                },

                reasonForRequest: {
                    required,
                },

                condition: {
                    required,
                },

                disclaimer: {
                    required,
                    sameAs: sameAs(() => true),
                },
            },
        };
    },

    methods: {
        getClient() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(
                        `/service/Clients?$filter=Id eq ${this.client.id}&$select=Id,Name,LogoImageUrl,TemplateFooter`
                    )
                    .then((response) => {
                        this.client.name = response.data.value[0].Name;
                        this.client.logoImageUrl = response.data.value[0].LogoImageUrl;
                        this.client.footer = response.data.value[0].TemplateFooter;

                        resolve();
                    })
                    .catch(reject);
            });
        },

        getStorefront() {
            this.isLoading = true;
            StorefrontService.getById(this.widget.agencyId)
                .then((response) => {
                    this.populateStoreData(response);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while fetching information.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        populateStoreData(response) {
            const data = response.data.data;
            const featuredItems = JSON.parse(data.storefront[0].FeactureItemJson);

            this.storefront.disclaimer = data.storefront[0].Disclaimer;
            this.storefront.name = data.storefront[0].Name;
            this.storefront.logoImageUrl = data.storefront[0].LogoImageUrl;
            this.storefront.description = data.storefront[0].OfferDescription;
            this.storefront.resources = Array.isArray(featuredItems)
                ? featuredItems.filter((i) => i.id)
                : [];
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget, this.widget.clientId);
        },

        getIsValidField(field) {
            const isValid = this.$v.client[field].$invalid && this.$v.client[field].$dirty;
            return isValid;
        },

        showSuccessPopup() {
            const actions = {
                confirm: {
                    label: 'View Status',
                    action: () => {
                        this.$router.push(`${this.routeBaseUrl}/Account/Partners`);
                    },
                },

                tertiary: {
                    label: 'Close',
                    action: () => {},
                },
            };

            this.$modal.show(
                AppPopup,
                {
                    type: 'success-popup success',
                    title: 'Success!',
                    content:
                        '<p>Your application has been submitted. <br /><br />Check on the status of your request via the Partners page.</p>',
                    actions: actions,
                },
                {
                    height: this.$helpers.setPopupHeight(),
                    width: this.$helpers.setPopupWidth(),
                }
            );
        },

        addProtocole(x) {
            if (x && x !== '#' && x.indexOf('http') === -1) {
                x = 'http://' + x;
            }
            return x;
        },

        onClickApplyAction() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }
            this.loadingRequest = true;
            const objectToSend = {
                ClientId: this.client.id,
                AgencyId: this.agendyId,
                Website: this.addProtocole(this.client.website),
                Contact: this.client.contact,
                ContactEmail: this.client.contactEmail,
                ContactPhoneNumber: this.client.phoneNumber,
                ReasonForRequest: this.client.reasonForRequest,
                AgreeTermsConditions: this.client.disclaimer,
                CompanyName: this.client.name,
                Origin: 'client',
            };

            PartnerApplicationService.create(objectToSend)
                .then(() => {
                    this.closeWidget();
                    this.showSuccessPopup();
                    EventBus.$emit('update-application-status');
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while creating your request.',
                        { error },
                    );
                })
                .finally(() => {
                    this.loadingRequest = false;
                });
        },
    },
};
</script>

<style>
.apply-to-partnership-widget .prospect-text {
    color: #877d88;
}

.apply-to-partnership-widget .widget-container > * {
    margin-bottom: 20px;
}

.apply-to-partnership-widget .checkbox-section.has-errors .app-checkbox .checkmark {
    background: none;
}

.apply-to-partnership-widget .checkbox-section.has-errors .app-checkbox .checkmark,
.apply-to-partnership-widget .checkbox-section.has-errors .app-checkbox input[type='checkbox'] {
    border: 2px solid #f30070;
    border-radius: 4px;
}

.apply-to-partnership-widget .checkbox-section {
    display: flex;
    gap: 20px;
    vertical-align: middle;
}

.apply-to-partnership-widget .vendor-label {
    font-weight: 600;
}

.apply-to-partnership-widget .error-message-request {
    display: block;
    margin: 0.8em 0 1.25em 0;
    font-size: 12px;
    color: #f30070;
}

.apply-to-partnership-widget textarea:focus {
    outline: none;
}

.apply-to-partnership-widget .text-area {
    resize: none;
    color: #382153;
}

.apply-to-partnership-widget .text-area::placeholder {
    color: #cac6cb;
}

.apply-to-partnership-widget .custom-textarea {
    display: flex;
    flex-direction: column;
    font-weight: 400;
    gap: 2px;
    font: normal 16px/1em 'Roboto';
    width: 100%;
}

.apply-to-partnership-widget .gray-container {
    background: #fafafa;
    font: normal 16px/1em 'Roboto';
    width: 100%;
    border: 2px solid #cac6cb;
    padding: 7px 15px;
    border-radius: 20px;
    color: #382153;
}

.apply-to-partnership-widget .title-label {
    display: block;
    font: 500 16px/1em 'Roboto';
    margin-bottom: 7px;
    color: #382153;
}

.apply-to-partnership-widget .item {
    display: flex;
    align-items: center;
    background: #f7f7f8;
    font: normal 16px/1em 'Roboto';
    width: 100%;
    border: 2px solid #cac6cb;
    padding: 3px 15px;
    border-radius: 20px;
    color: #382153;
    outline: none;
}

.apply-to-partnership-widget .app-input input {
    background: #f7f7f8;
}

.apply-to-partnership-widget .logo-img {
    height: 28px;
    width: 28px;
    margin-right: 10px;
    border-radius: 50%;
    background-size: contain;
    background-position: top left;
    background-repeat: no-repeat;
}

.apply-to-partnership-widget .image-header {
    display: flex;
    justify-content: center;
}

.apply-to-partnership-widget .input-label {
    font-weight: 600;
    display: flex;
    align-items: center;
}

.apply-to-partnership-widget .cancel-btn {
    background: transparent;
    border: 1px solid #cac6cb;
    border-radius: 20px;
    width: 127px;
    font: normal normal medium 14px/25px Roboto;
    letter-spacing: 0px;
    color: #382153;
}

.apply-to-partnership-widget .cancel-btn:hover {
    background: #e9e9ef;
    border: 2px solid #cac6cb;
}

.apply-to-partnership-widget .save-btn {
    width: 148px;
}

.apply-to-partnership-widget .app-input.error.has-errors input,
.apply-to-partnership-widget .gray-container.has-errors {
    border: 2px solid #f30070;
}

.apply-to-partnership-widget .title-label.has-errors {
    color: #f30070;
}
</style>
